import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { lightTheme } from '@spotify-internal/encore-foundation';
import { capitalize } from "../utils.js";
import { calcAccessibleContrast, getHighContrastColor, getHighlightAndPressColors, rgbaToHex } from "./color.js";
export var semanticBackgroundColors = {
  backgroundBase: 'backgroundBase',
  backgroundHighlight: 'backgroundHighlight',
  backgroundPress: 'backgroundPress',
  backgroundElevatedBase: 'backgroundElevatedBase',
  backgroundElevatedHighlight: 'backgroundElevatedHighlight',
  backgroundElevatedPress: 'backgroundElevatedPress',
  backgroundTintedBase: 'backgroundTintedBase',
  backgroundTintedHighlight: 'backgroundTintedHighlight',
  backgroundTintedPress: 'backgroundTintedPress'
};
export var semanticTextColors = {
  textBase: 'textBase',
  textSubdued: 'textSubdued',
  textBrightAccent: 'textBrightAccent',
  textNegative: 'textNegative',
  textWarning: 'textWarning',
  textPositive: 'textPositive',
  textAnnouncement: 'textAnnouncement'
};
export var semanticEssentialColors = {
  essentialBase: 'essentialBase',
  essentialSubdued: 'essentialSubdued',
  essentialBrightAccent: 'essentialBrightAccent',
  essentialNegative: 'essentialNegative',
  essentialWarning: 'essentialWarning',
  essentialPositive: 'essentialPositive',
  essentialAnnouncement: 'essentialAnnouncement'
};
export var semanticDecorativeColors = {
  decorativeBase: 'decorativeBase',
  decorativeSubdued: 'decorativeSubdued'
};
export var semanticForegroundColors = _objectSpread(_objectSpread(_objectSpread({}, semanticTextColors), semanticEssentialColors), semanticDecorativeColors);
export var genericSemanticForegroundColors = {
  base: 'base',
  subdued: 'subdued',
  brightAccent: 'brightAccent',
  negative: 'negative',
  warning: 'warning',
  positive: 'positive',
  announcement: 'announcement'
};
export var semanticColors = _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, semanticBackgroundColors), semanticTextColors), semanticEssentialColors), semanticDecorativeColors);
/**
 * Theme default constants for Encore Web
 * */
export var defaultTheme = lightTheme;
export var defaultColorSetName = 'base';
export var defaultColorSet = defaultTheme[defaultColorSetName];
export var colorSets = Object.keys(defaultTheme);

/**
 * Takes a deeply nested object and returns a flattend map of all the values
 * with the keys camelCased.
 * @param obj - a nested object of string values
 * @param string - the parent key of an object used for recursion.
 * @returns an object with camcelCased keys and string values
 */
var _getFlatCamelCaseMap = function getFlatCamelCaseMap(obj) {
  var previousKey = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
  return Object.entries(obj).reduce(function (prevObj, _ref) {
    var _ref2 = _slicedToArray(_ref, 2),
      key = _ref2[0],
      value = _ref2[1];
    var camelKey = key;
    if (previousKey !== '') {
      camelKey = "".concat(previousKey).concat(capitalize(key));
    }
    if (typeof value === 'object') {
      return _objectSpread(_objectSpread({}, prevObj), _getFlatCamelCaseMap(value, camelKey));
    } else if (typeof value === 'string') {
      prevObj[camelKey] = value;
      return prevObj;
    }
    return prevObj;
  }, {});
};
export { _getFlatCamelCaseMap as getFlatCamelCaseMap };
function camelToKebab(inputString) {
  return inputString.replace(/[A-Z0-9]/g, function (m) {
    return "-".concat(m.toLowerCase());
  });
}
export function makeColorSetClass(colorSet) {
  return colorSet ? "encore-".concat(camelToKebab(colorSet), "-set") : '';
}
export function makeCampaignCategoryColorSetClass(colorSet) {
  if (!colorSet) return '';
  var removedSetKeyword = colorSet.replace('Set', '');
  return "encore-".concat(camelToKebab(removedSetKeyword), "-set");
}
/**
 * Appends colorSet classname to the passed className string
 * @param colorSet - A colorSet (eg. 'base', 'negative', 'brightAccent')
 * @param className - Other classnames passed from the elements
 * @returns string of passed classnames plus the colorset class
 */
export function addColorSet(colorSet, className) {
  if (colorSet in lightTheme) {
    return [makeColorSetClass(colorSet), className].join(' ').trim();
  }
  return [makeCampaignCategoryColorSetClass(colorSet), className].join(' ').trim();
}

/**
 * Returns the css variable format of a semanticColor
 * @param semanticColor - a semanticColor (eg 'textBase,' 'decorativeSubdued')
 * @returns a css variable in kebabcase format with a '--' prefix
 */
export function cssColorVar(semanticColor) {
  return "--".concat(camelToKebab(semanticColor));
}

/**
 * Returns an array of css variables and hex values that can be applied as custom styles via styled-components
 * @param colorSetObject
 */
export function cssColorSet(colorSetObject) {
  return Object.entries(_getFlatCamelCaseMap(colorSetObject)).map(function (_ref3) {
    var _ref4 = _slicedToArray(_ref3, 2),
      token = _ref4[0],
      hex = _ref4[1];
    return "\n      ".concat(cssColorVar(token), ": ").concat(hex, " !important;\n    ");
  });
}

/**
 * Returns an object of css variables and hex values that can be applied as custom styles via the style attribute
 * @param colorSetObject
 */
export function cssColorSetObject(colorSetObject) {
  return Object.entries(_getFlatCamelCaseMap(colorSetObject)).reduce(function (acc, _ref5) {
    var _ref6 = _slicedToArray(_ref5, 2),
      token = _ref6[0],
      hex = _ref6[1];
    acc[cssColorVar(token)] = "".concat(hex);
    return acc;
  }, {});
}
var flattenedDefaultColorSet = _getFlatCamelCaseMap(defaultColorSet);

/**
 * Get a CSS value that can be used when authoring CSS color rules
 * @param semanticColor - a semanticColor (eg 'textBase,' 'decorativeSubdued')
 * @param [fallbackColor] - a valid CSS <color> value as fallback
 * @returns a string in the form of `var(--semantic-color-variable, <fallback>)`
 */
export function cssColorValue(semanticColor, fallbackColor) {
  var fallback = flattenedDefaultColorSet[semanticColor];
  if (fallbackColor) {
    fallback = fallbackColor;
  }
  return "var(".concat(cssColorVar(semanticColor), ", ").concat(fallback, ")");
}

/**
 * Convert five semantic color values in RGBA format into a full dynamic color set.
 *
 * This utility function does not perform color contrast checks because it assumes
 * the dynamic color parameters supplied are generated by a background service
 * which uses the APCA contrast algorithm.
 *
 * By default, this function returns a fallback gray color set. Otherwise, a complete
 * set of RGBA colors arguments is required.
 *
 * @param backgroundRgba
 * @param backgroundTintedRgba
 * @param textRgba
 * @param textSubduedRgba
 * @param textBrightAccentHexRgba
 */

export var createDynamicColorSet = function createDynamicColorSet() {
  var _ref7 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
      backgroundBase: {
        r: 53,
        g: 53,
        b: 53,
        a: 255
      },
      backgroundTinted: {
        r: 86,
        g: 86,
        b: 86,
        a: 255
      },
      textBase: {
        r: 255,
        g: 255,
        b: 255,
        a: 255
      },
      textSubdued: {
        r: 205,
        g: 205,
        b: 205,
        a: 205
      },
      textBrightAccent: {
        r: 30,
        g: 215,
        b: 96,
        a: 255
      }
    },
    backgroundBase = _ref7.backgroundBase,
    backgroundTinted = _ref7.backgroundTinted,
    textBase = _ref7.textBase,
    textSubdued = _ref7.textSubdued,
    textBrightAccent = _ref7.textBrightAccent;
  var backgroundBaseHex = rgbaToHex(backgroundBase);
  var backgroundTintedHex = rgbaToHex(backgroundTinted);
  var textBaseHex = rgbaToHex(textBase);
  var textSubduedHex = rgbaToHex(textSubdued);
  var textBrightAccentHex = rgbaToHex(textBrightAccent);
  var _getHighlightAndPress = getHighlightAndPressColors(backgroundBaseHex, textBaseHex),
    backgroundHighlightHex = _getHighlightAndPress.highlight,
    backgroundPressHex = _getHighlightAndPress.press;
  var _getHighlightAndPress2 = getHighlightAndPressColors(backgroundTintedHex, textBaseHex),
    backgroundTintedHighlightHex = _getHighlightAndPress2.highlight,
    backgroundTintedPressHex = _getHighlightAndPress2.press;
  return {
    background: {
      base: backgroundBaseHex.toUpperCase(),
      highlight: backgroundHighlightHex.toUpperCase(),
      press: backgroundPressHex.toUpperCase(),
      elevated: {
        base: backgroundBaseHex.toUpperCase(),
        highlight: backgroundHighlightHex.toUpperCase(),
        press: backgroundPressHex.toUpperCase()
      },
      tinted: {
        base: backgroundTintedHex.toUpperCase(),
        highlight: backgroundTintedHighlightHex.toUpperCase(),
        press: backgroundTintedPressHex.toUpperCase()
      }
    },
    text: {
      base: textBaseHex.toUpperCase(),
      subdued: textSubduedHex.toUpperCase(),
      brightAccent: textBrightAccentHex.toUpperCase(),
      negative: textBaseHex.toUpperCase(),
      warning: textBaseHex.toUpperCase(),
      positive: textBaseHex.toUpperCase(),
      announcement: textBaseHex.toUpperCase()
    },
    essential: {
      base: textBaseHex.toUpperCase(),
      subdued: textSubduedHex.toUpperCase(),
      brightAccent: textBrightAccentHex.toUpperCase(),
      negative: textBaseHex.toUpperCase(),
      warning: textBaseHex.toUpperCase(),
      positive: textBaseHex.toUpperCase(),
      announcement: textBaseHex.toUpperCase()
    },
    decorative: {
      base: textBaseHex.toUpperCase(),
      subdued: textSubduedHex.toUpperCase()
    }
  };
};

/**
 * Create a simple color set from a custom color, that meets contrast requirements
 * @param backgroundHex
 * @param [suggestedForegroundHex]
 */
export var createBasicColorSet = function createBasicColorSet(backgroundHex, suggestedForegroundHex) {
  var accessibleContrast = 4.45;
  var foregroundHex = suggestedForegroundHex && calcAccessibleContrast(backgroundHex, suggestedForegroundHex) >= accessibleContrast ? suggestedForegroundHex : getHighContrastColor(backgroundHex);
  var _getHighlightAndPress3 = getHighlightAndPressColors(backgroundHex, foregroundHex),
    backgroundHighlightHex = _getHighlightAndPress3.highlight,
    backgroundPressHex = _getHighlightAndPress3.press;
  return {
    background: {
      base: backgroundHex.toUpperCase(),
      highlight: backgroundHighlightHex.toUpperCase(),
      press: backgroundPressHex.toUpperCase(),
      elevated: {
        base: backgroundHex.toUpperCase(),
        highlight: backgroundHighlightHex.toUpperCase(),
        press: backgroundPressHex.toUpperCase()
      },
      tinted: {
        base: backgroundHex.toUpperCase(),
        highlight: backgroundHighlightHex.toUpperCase(),
        press: backgroundPressHex.toUpperCase()
      }
    },
    text: {
      base: foregroundHex.toUpperCase(),
      subdued: foregroundHex.toUpperCase(),
      brightAccent: foregroundHex.toUpperCase(),
      negative: foregroundHex.toUpperCase(),
      warning: foregroundHex.toUpperCase(),
      positive: foregroundHex.toUpperCase(),
      announcement: foregroundHex.toUpperCase()
    },
    essential: {
      base: foregroundHex.toUpperCase(),
      subdued: foregroundHex.toUpperCase(),
      brightAccent: foregroundHex.toUpperCase(),
      negative: foregroundHex.toUpperCase(),
      warning: foregroundHex.toUpperCase(),
      positive: foregroundHex.toUpperCase(),
      announcement: foregroundHex.toUpperCase()
    },
    decorative: {
      base: foregroundHex.toUpperCase(),
      subdued: foregroundHex.toUpperCase()
    }
  };
};